import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MENU_ROUTES } from 'App/components/sidebar/constants';
import { logError } from 'App/services/faroService';
import { hasAuthToken } from 'App/services/idpTokenService';
import { isSiteShutdown } from 'App/services/siteShutDownService';
import {
  CONTACT_US_DESCRIPTION_MAX_CHARACTERS,
  CONTACT_US_DESCRIPTION_MIN_CHARACTERS,
  HELP_PAGE_QUERY_PARAMS,
  INIT_FORM_DATA_TYPES,
  SALESFORCE_API_ENUMS,
  SALESFORCE_DEVICE_PLATFORM_ENUMS
} from 'Pages/help/subPages/contactUs/constants';
import ContactUsForm from 'Pages/help/subPages/contactUs/form/ContactUsForm';
import {
  FormContainer,
} from 'Pages/help/subPages/contactUs/form/style';
import {
  BannerContainer,
  ButtonContainer,
  ContactUsContainer,
  ErrorContainer,
  HelperContainer,
  InputFieldWrapper,
  SubmittedContent,
  SuccessfulNote
} from 'Pages/help/subPages/contactUs/style';
import { CONTACT_US_TEST_IDS } from 'Pages/help/testId';
import { Banner, BannerContextTypes } from 'Shared/components/design/banner/Banner';
import { Button, ButtonSizes, ButtonWidth, ContextTypes } from 'Shared/components/design/button/Button';
import { SelectFieldTypes } from 'Shared/components/design/select/Select';
import { TextAreaTypes } from 'Shared/components/design/textArea/TextArea';
import { TextFieldTypes  } from 'Shared/components/design/textField/TextField';
import {ICON_IDS} from "Shared/components/icons/constants";
import { COUNTRY_CODE_UK } from 'Shared/constants';
import { useKeyDown } from 'Shared/hooks/useKeyDown';
import { useQueryParam } from 'Shared/hooks/useQueryParam';
import { ANALYTICS_EVENT_ATTRIBUTES, ANALYTICS_REFERRER_TYPES } from 'Shared/services/analytics/constants';
import { analyticsTrackContactFormSubmitEnd } from 'Shared/services/analytics/events/contactFormSubmitEnd';
import { APIResponse } from 'Shared/services/apiInterface';
import { isMobile } from 'Shared/services/deviceService';
import { isValidEmail } from 'Shared/services/inputValidationService';
import {
  createTicket,
  TicketModel
} from 'Shared/services/mct/api/mctApi';
import { getUserInfo } from 'Shared/services/userActivation/api/idpApiService';

enum SLIDES {
  SUBMIT,
  SUCCESS,
  ERROR
}

/**
 * Renders ContactUs component
 */
function ContactUs(): JSX.Element {
  const { t }                                           = useTranslation('accountHelp');
  const querySurveyId                                   = useQueryParam(HELP_PAGE_QUERY_PARAMS.SURVEY_ID);
  const querySurveyStatus                               = useQueryParam(HELP_PAGE_QUERY_PARAMS.SURVEY_STATUS);
  const queryIssueType                                  = useQueryParam(HELP_PAGE_QUERY_PARAMS.TYPE);
  const queryZipCode                                    = useQueryParam(HELP_PAGE_QUERY_PARAMS.ZIP_CODE);
  const [firstLastName, setFirstLastName]               = useState<string>('');
  const [emailAddress, setEmailAddress]                 = useState<string>('');
  const [subject, setSubject]                           = useState<string>('');
  const [initialSubject, setInitialSubject]             = useState<string>('');
  const [category, setCategory]                         = useState<string>(t('ContactUs--categoryOptions-pleaseSelect'));
  const [description, setDescription]                   = useState<string>('');
  const [hasDescriptionError, setHasDescriptionError]   = useState<boolean>(false);
  const [nameFieldType, setNameFieldType]               = useState<TextFieldTypes>(TextFieldTypes.DEFAULT);
  const [emailFieldType, setEmailFieldType]             = useState<TextFieldTypes>(TextFieldTypes.DEFAULT);
  const [subjectFieldType, setSubjectFieldType]         = useState<TextFieldTypes>(TextFieldTypes.DEFAULT);
  const [descriptionFieldType, setDescriptionFieldType] = useState<TextAreaTypes>(TextAreaTypes.DEFAULT);
  const [selectFieldType, setSelectFieldType]           = useState<SelectFieldTypes>(SelectFieldTypes.DEFAULT);
  const [disableButton, setDisableButton]               = useState<boolean>(false);
  const [slide, setSlide]                               = useState<SLIDES>(SLIDES.SUBMIT);
  const [isBannerVisible, setIsBannerVisible]           = useState<boolean>(true);
  const [active, setActive]                             = useState<boolean>(false);
  const [userCountry, setUserCountry]                   = useState<string>('');
  const [isLoading, setIsLoading]                       = useState<boolean>(false);
  const [isUserLoggedIn, setIsUserLoggedIn]             = useState<boolean>(false);
  const [clickedSubmit, setClickedSubmit]               = useState<boolean>(false);
  const navigateTo                                      = useNavigate();


  /**
   * Mapping of initialization form data for different issue types.
   * @type {Object}
   */
  const initialFormValues = {
    [INIT_FORM_DATA_TYPES.SURVEY_ISSUE]: {
      category      : t('ContactUs--categoryOptions-SurveysExperience'),
      getSubject    : (surveyId?: string) => t('ContactUs--categoryOptions-surveyIssueSubject', { surveyId }),
      getDescription: () => ''
    },
    [INIT_FORM_DATA_TYPES.PULSE]: {
      category      : t('ContactUs--categoryOptions-Pulse'),
      getSubject    : () => '',
      getDescription: () => ''
    },
    [INIT_FORM_DATA_TYPES.EDIT_NAME]: {
      category      : t('ContactUs--categoryOptions-profileInformation'),
      getSubject    : () => t('ContactUs--categoryOptions-editNameSubject'),
      getDescription: () => ''
    },
    [INIT_FORM_DATA_TYPES.MAX_REDEMPTION_POINTS]: {
      category      : t('ContactUs--categoryOptions-Redemption'),
      getSubject    : () => t('ContactUs--categoryOptions-maxRedemptionSubject'),
      getDescription: () => t('ContactUs--categoryOptions-maxRedemptionDesc')
    },
    [INIT_FORM_DATA_TYPES.EDIT_ZIP_CODE]: {
      category      : t('ContactUs--categoryOptions-profileInformation'),
      getSubject    : () => t('ContactUs--categoryOptions-editZipCodeSubject'),
      getDescription: (zipCode?: string) => t('ContactUs--categoryOptions-editZipCodeDesc', { zipCode: zipCode || '' })
    },
  };

  /**
   * Clears form data upon successful ticket submit
   */
  const clearFormData = (): void => {
    if (!isUserLoggedIn) {
      setFirstLastName('');
      setEmailAddress('');
    }

    setCategory(t('ContactUs--categoryOptions-payPal'));
    setDescription('');
    setSubject('');
  };

  /**
   * Initialize form data based on query parameters.
   */
  const initFormData = (issueType: string, surveyId?: string | null, zipCode?: string | null): void => {
    if (!issueType) {
      return;
    }
    const initialValues = initialFormValues[issueType];
    if (initialValues) {
      setCategory(initialValues.category);
      if (surveyId) {
        setInitialSubject(initialValues.getSubject(surveyId));
        setSubject(initialValues.getSubject(surveyId));
      }

      if (initialValues.category === t('ContactUs--categoryOptions-profileInformation')) {
        if (issueType === INIT_FORM_DATA_TYPES.EDIT_ZIP_CODE && zipCode) {
          setSubject(initialValues.getSubject());
          setDescription(initialValues.getDescription(zipCode));
        } else {
          setSubject(initialValues.getSubject());
        }
      }

      if (initialValues.category === t('ContactUs--categoryOptions-Redemption')) {
        setSubject(initialValues.getSubject());
        setDescription(initialValues.getDescription());
      }
    }
  };

  useEffect(() => {
    if (!queryIssueType) {
      return;
    }

    initFormData(queryIssueType, querySurveyId, queryZipCode); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySurveyId, queryIssueType, queryZipCode]);

  /**
   * Check user state loggedIn/out
   */
  useEffect(() => {
    const isUserLoggedIn = hasAuthToken();

    if (isUserLoggedIn) {
      setIsUserLoggedIn(true);

      getUserInfo().then((response: APIResponse) => {
        setFirstLastName(response.data.firstName + ' ' + response.data.lastName);
        setEmailAddress(response.data.email);
        setUserCountry(response.data.country);
        setActive(true);
      }).catch(error => {
        setSlide(SLIDES.ERROR);
        logError('getUserInfo', error);
      });
    } else {
      setActive(true);
    }
  },[active]);

  /**
   * Form Validation
   */
  const isFormContentValid = (): boolean => {
    setEmailFieldType(TextFieldTypes.DEFAULT);
    setSubjectFieldType(TextFieldTypes.DEFAULT);
    setNameFieldType(TextFieldTypes.DEFAULT);
    setDescriptionFieldType(TextAreaTypes.DEFAULT);

    const isEmailFormatValid = isValidEmail(emailAddress);

    if (firstLastName === '') {
      setNameFieldType(TextFieldTypes.ERROR);
    }

    if (emailAddress === '' ) {
      setEmailFieldType(TextFieldTypes.ERROR);
    }

    if (emailAddress.length !== 0) {
      isEmailFormatValid ? setEmailFieldType(TextFieldTypes.DEFAULT) : setEmailFieldType(TextFieldTypes.ERROR);
    }

    if (category === t('ContactUs--categoryOptions-pleaseSelect')) {
      setSelectFieldType(SelectFieldTypes.ERROR);
    }

    if (subject === '') {
      setSubjectFieldType(TextFieldTypes.ERROR);
    }

    if (description.length > CONTACT_US_DESCRIPTION_MAX_CHARACTERS || description === '') {
      setDescriptionFieldType(TextAreaTypes.ERROR);
    }

    if (description.length < CONTACT_US_DESCRIPTION_MIN_CHARACTERS) {
      setDescriptionFieldType(TextAreaTypes.ERROR);
      setHasDescriptionError(true);
    }

    if (emailFieldType !== TextFieldTypes.ERROR
      && description !== ''
      && category !== t('ContactUs--categoryOptions-pleaseSelect')
      && descriptionFieldType !== TextAreaTypes.ERROR
      && subjectFieldType !== TextFieldTypes.ERROR
      && firstLastName !== ''
      && !hasDescriptionError
    ) {
      setClickedSubmit(false);
      return true;
    } else {
      setClickedSubmit(false);
      return false;
    }
  };

  /**
   * Submits user ticket to Salesforce
   */
  const submitTicket = (): void => {
    const country = isSiteShutdown() ? COUNTRY_CODE_UK : userCountry;
    const descriptionField = initialSubject && initialSubject !== subject
      ? `Subject: ${initialSubject}\nName: ${firstLastName}\n${description}`
      : `Name: ${firstLastName}\n${description}`;

    const userData: TicketModel = {
      status        : SALESFORCE_API_ENUMS.TICKET_STATUS_NEW,
      origin        : SALESFORCE_API_ENUMS.TICKET_ORIGIN,
      subject       : subject,
      description   : descriptionField,
      country,
      devicePlatform: isMobile() ? SALESFORCE_DEVICE_PLATFORM_ENUMS.SMARTPHONE: SALESFORCE_DEVICE_PLATFORM_ENUMS.DESKTOP,
      email         : emailAddress,
      category      : category,
      suppliedName  : firstLastName.split(' ')[0], // submit only the user's first name
      surveyStatus  : querySurveyStatus || '',
    };

    const analyticsData = {
      [ANALYTICS_EVENT_ATTRIBUTES.sub_type]     : category,
      [ANALYTICS_EVENT_ATTRIBUTES.referrer]     : queryIssueType ? ANALYTICS_REFERRER_TYPES.activity_history: ANALYTICS_REFERRER_TYPES.none,
      [ANALYTICS_EVENT_ATTRIBUTES.survey_id]    : querySurveyId || '',
      [ANALYTICS_EVENT_ATTRIBUTES.survey_status]: querySurveyStatus || '',
    };

    setIsLoading(true);
    setDisableButton(true);

    createTicket(userData)
      .then(() => {
        setClickedSubmit(false);
        setDisableButton(false);
        setIsLoading(false);
        setSlide(SLIDES.SUCCESS);
        analyticsTrackContactFormSubmitEnd(analyticsData);
      })
      .catch(error => {
        setClickedSubmit(false);
        setDisableButton(false);
        setIsLoading(false);
        setSlide(SLIDES.ERROR);
        logError('getUserInfo', error);
      });
  };

  /**
   * Trigger form submit on pressing enter key
   */
  useKeyDown(() => {
    handleSubmit();
  });

  /**
   * Create ticket in Salesforce
   */
  const handleSubmit = (): void => {
    setClickedSubmit(true);
    if (isFormContentValid()) {
      submitTicket();
    }
  };

  /**
   * Returns the user back submit slide after error is encountered
   */
  const handleErrorSubmit = (): void => {
    setSlide(SLIDES.SUBMIT);
  };

  /**
   * Return user to submit ticket slide upon successful complete
   */
  const handleSubmitComplete = (): void => {
    setSlide(SLIDES.SUBMIT);
    navigateTo(MENU_ROUTES.ACCOUNT_SUPPORT);
    clearFormData();
    setIsBannerVisible(true);
  };

  /**
   * Description and email field validation
   */
  useEffect(() => {
    const isEmailFormatValid = isValidEmail(emailAddress);

    if (firstLastName === '' && clickedSubmit) {
      setNameFieldType(TextFieldTypes.ERROR);
    } else if (firstLastName !== '') {
      setNameFieldType(TextFieldTypes.DEFAULT);
    }

    if (subject === '' && clickedSubmit) {
      setSubjectFieldType(TextFieldTypes.ERROR);
    } else if (subject !== '') {
      setSubjectFieldType(TextFieldTypes.DEFAULT);
    }

    if (category === t('ContactUs--categoryOptions-pleaseSelect') && clickedSubmit ) {
      setSelectFieldType(SelectFieldTypes.ERROR);
    } else if (category !== t('ContactUs--categoryOptions-pleaseSelect')) {
      setSelectFieldType(SelectFieldTypes.DEFAULT);
    }

    if (description.length > CONTACT_US_DESCRIPTION_MAX_CHARACTERS || (description === '' &&  clickedSubmit) || (description !== '' && description.length < CONTACT_US_DESCRIPTION_MIN_CHARACTERS)) {
      setDescriptionFieldType(TextAreaTypes.ERROR);
      setHasDescriptionError(true);
    } else if (description !== '' && description.length <= CONTACT_US_DESCRIPTION_MAX_CHARACTERS && description.length >= CONTACT_US_DESCRIPTION_MIN_CHARACTERS ) {
      setDescriptionFieldType(TextAreaTypes.DEFAULT);
      setHasDescriptionError(false);
    }

    if (emailAddress.length !== 0) {
      isEmailFormatValid ? setEmailFieldType(TextFieldTypes.DEFAULT) : setEmailFieldType(TextFieldTypes.ERROR);
    }

  },[category, clickedSubmit, description, descriptionFieldType, emailAddress, firstLastName, subject, t]);

  /**
   * Displays remaining characters for description user entry
   */
  const getHelperText = (): string => {
    return `${CONTACT_US_DESCRIPTION_MAX_CHARACTERS - description.length} characters remaining`;
  };

  /**
   * Displays remaining characters for description user entry
   */
  const getCharsAmountErrorText = (): string => {
    return `Must be a minimum of ${CONTACT_US_DESCRIPTION_MIN_CHARACTERS} characters`;
  };

  /**
   * Renders the banner
   */
  const renderBanner = (): JSX.Element => {
    return (
      <BannerContainer isVisible>
        <Banner
          fillParent     = {true}
          context        = {BannerContextTypes.LIGHT_WARNING}
          icon           = {ICON_IDS.VERIFIED_FILL_1}
          text           = {
            <p>
              {t('ContactUs--banner-text')}<a href={process.env.REACT_APP_ONE_TRUST_DO_NOT_SELL_URL} target="_blank" rel="noreferrer">{t('ContactUs--banner-text-link')}</a>
            </p>
          }
        />
      </BannerContainer>
    );
  };

  const renderForm = (): JSX.Element => {
    return (
      <>
        <ContactUsForm
          nameCallback          = {setFirstLastName}
          emailCallback         = {setEmailAddress}
          subjectCallback       = {setSubject}
          categoryCallback      = {setCategory}
          descriptionCallback   = {setDescription}
          emailFieldType        = {emailFieldType}
          descriptionFieldType  = {descriptionFieldType}
          firstLastName         = {firstLastName}
          userEmail             = {emailAddress}
          isUserLoggedIn        = {isUserLoggedIn}
          subjectFieldType      = {subjectFieldType}
          nameFieldType         = {nameFieldType}
          selectFieldType       = {selectFieldType}
          disableSubmitCallback = {setDisableButton}
          defaultValues         = {{
            subject    : subject,
            category   : category,
            description: description,
          }}
        />
        <HelperContainer isError={(descriptionFieldType === TextAreaTypes.ERROR)}>
          {hasDescriptionError ? getCharsAmountErrorText() : getHelperText()}
        </HelperContainer>
        <ButtonContainer>
          <Button
            size      = {ButtonSizes.LARGE}
            width     = {ButtonWidth.FULL}
            context   = {ContextTypes.PRIMARY}
            disabled  = {disableButton}
            onClick   = {handleSubmit}
            isLoading = {isLoading}
          >
            {t('ContactUs--submit-buttonLabel')}
          </Button>
        </ButtonContainer>
      </>
    );
  };

  /**
   * Renders the initial Submit slide
   */
  const renderSubmitSlide = (): JSX.Element => {
    return(
      <>
        {renderBanner()}
        {renderForm()}
      </>
    );
  };

  /**
   * Renders the success slide
   */
  const renderSuccessSlide = (): JSX.Element => {
    return(
      <>
        <BannerContainer isVisible={isBannerVisible}>
          <Banner
            fillParent = {true}
            context    = {BannerContextTypes.INFO}
            icon       = {ICON_IDS.CHECK_FILL}
            close      = {true}
            text       = {
              <p>
                {t('ContactUs--successBanner-text')}
              </p>
            }
          />
        </BannerContainer>
        <FormContainer>
          <InputFieldWrapper>
            <span>
              {t('ContactUs--inputLabel-name')}
            </span>
            <SubmittedContent shouldWrap={false}>
              { firstLastName }
            </SubmittedContent>
          </InputFieldWrapper>
          <InputFieldWrapper>
            <span>
              {t('ContactUs--inputLabel-email')}
            </span>
            <SubmittedContent shouldWrap={false}>
              { emailAddress }
            </SubmittedContent>
          </InputFieldWrapper>
          <InputFieldWrapper>
            <span>
              {t('ContactUs--inputLabel-category')}
            </span>
            <SubmittedContent shouldWrap={false}>
              { category }
            </SubmittedContent>
          </InputFieldWrapper>
          <InputFieldWrapper>
            <span>
              {t('ContactUs--inputLabel-subject')}
            </span>
            <SubmittedContent shouldWrap={false}>
              { subject }
            </SubmittedContent>
          </InputFieldWrapper>
          <InputFieldWrapper>
            <span>
              {t('ContactUs--inputLabel-description')}
            </span>
            <SubmittedContent shouldWrap={true}>
              { description}
            </SubmittedContent>
          </InputFieldWrapper>
          <SuccessfulNote>
            {t('ContactUs--success-note')}
          </SuccessfulNote>
          <ButtonContainer>
            <Button
              size     = {ButtonSizes.LARGE}
              width    = {ButtonWidth.FULL}
              context  = {ContextTypes.SECONDARY}
              onClick  = {handleSubmitComplete}
            >
              {t('ContactUs--success-buttonLabel')}
            </Button>
          </ButtonContainer>
        </FormContainer>
      </>
    );
  };

  /**
   * Renders the error message
   */
  const renderErrorSlide = (): JSX.Element => {
    return(
      <ErrorContainer>
        <h1>
          {t('ContactUs--error-title')}
        </h1>
        <p>
          {t('ContactUs--error-paragraph')}
        </p>
        <ButtonContainer>
          <Button
            size      = {ButtonSizes.LARGE}
            width     = {ButtonWidth.FULL}
            context   = {ContextTypes.PRIMARY}
            onClick   = {handleErrorSubmit}
            isLoading = {isLoading}
          >
          {t('ContactUs--error-buttonLabel')}
        </Button>
        </ButtonContainer>
      </ErrorContainer>
    );
  };

  /**
   * Conditionally renders slides
   */
  const renderContactUs = (): JSX.Element | undefined => {
    if (isUserLoggedIn && !active) {
      return;
    }

    switch(slide) {
      case SLIDES.SUBMIT:
        return renderSubmitSlide();
      case SLIDES.SUCCESS:
        return renderSuccessSlide();
      case SLIDES.ERROR:
        return renderErrorSlide();
    }
  };

  return (
    <ContactUsContainer data-testid={CONTACT_US_TEST_IDS.INDEX}>
      {renderContactUs()}
    </ContactUsContainer>
  );
}

export default ContactUs;
